import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { movieAPI, userAPI } from '../services/api';
import { useUserStore, useAuthStore } from '../stores/movieStore';
import type { Movie } from '../stores/movieStore';

const DetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [movie, setMovie] = useState<Movie | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toggleFavorite, isFavorite } = useUserStore();
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    if (id) fetchMovieDetail(id);
  }, [id]);

  const fetchMovieDetail = async (movieId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await movieAPI.getDetail(movieId);
      if (response.data) {
        setMovie(response.data);
      } else {
        setError('电影不存在');
      }
    } catch {
      setError('加载失败，请刷新重试');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('请先登录');
      return;
    }
    if (!movie) return;

    const added = toggleFavorite({
      videoId: movie.id,
      source: movie.sourceId || 'unknown',
      title: movie.title,
      poster: movie.poster,
      year: movie.year,
      rating: movie.rating,
      genre: movie.genre,
      description: movie.description,
    });

    if (added) {
      try { await userAPI.addFavorite(movie); } catch {}
      toast.success('已添加到收藏');
    } else {
      try { await userAPI.removeFavorite(movie.id); } catch {}
      toast.success('已取消收藏');
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 8) return 'text-green-400';
    if (rating >= 6) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error || !movie) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{error || '电影不存在'}</h2>
          <Link to="/" className="text-blue-400 hover:text-blue-300">返回首页</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="absolute inset-0 z-0">
        <img src={movie.backdrop || movie.poster} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900/95 to-slate-900" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/50">
                <img src={movie.poster} alt={movie.title} className="w-full aspect-[2/3] object-cover" />
                <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Link to={`/play/${movie.id}`} className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors">
                    <svg className="w-10 h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </Link>
                </div>
              </div>

              <div className="mt-4 flex gap-3">
                <Link to={`/play/${movie.id}`} className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all flex items-center justify-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>立即播放</span>
                </Link>
                <button onClick={handleToggleFavorite} className={`px-4 py-3 rounded-xl border transition-all ${isFavorite(movie.id, movie.sourceId || 'unknown') ? 'bg-red-500/20 border-red-500 text-red-400' : 'border-slate-600 text-gray-400 hover:border-red-500 hover:text-red-400'}`}>
                  <svg className={`w-6 h-6 ${isFavorite(movie.id, movie.sourceId || 'unknown') ? 'fill-current' : ''}`} fill={isFavorite(movie.id, movie.sourceId || 'unknown') ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">{movie.title}</h1>
              {movie.originalTitle && <p className="text-gray-400 text-lg mb-4">{movie.originalTitle}</p>}

              <div className="flex flex-wrap items-center gap-4 mb-6">
                <div className="flex items-center space-x-2">
                  <svg className={`w-6 h-6 ${getRatingColor(movie.rating)}`} fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className={`text-xl font-bold ${getRatingColor(movie.rating)}`}>{movie.rating.toFixed(1)}</span>
                  <span className="text-gray-500">/10</span>
                </div>
                <span className="text-gray-500">|</span>
                <span className="text-gray-300">{movie.year}</span>
                <span className="text-gray-500">|</span>
                <div className="flex flex-wrap gap-2">
                  {movie.genre.map((g) => (
                    <span key={g} className="px-3 py-1 bg-slate-700/50 text-gray-300 text-sm rounded-full">{g}</span>
                  ))}
                </div>
              </div>

              {movie.sources && movie.sources.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-white mb-4">播放源</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {movie.sources.map((source) => (
                      <Link key={source.id} to={`/play/${movie.id}?source=${source.id}`} className="p-4 rounded-xl border bg-slate-800/50 border-slate-700 hover:border-slate-500 transition-all">
                        <div className="text-white font-medium mb-1">{source.name}</div>
                        <div className="text-gray-400 text-sm">{source.quality} · {source.language}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-lg font-semibold text-white mb-4">剧情简介</h3>
                <p className="text-gray-300 leading-relaxed">{movie.description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailPage;
